/**
 * Recruiter Utilities - Consistent UUID-based recruiter matching
 * 
 * These utilities provide a standardized way to match candidates to recruiters
 * across the application, using UUID-based matching with name-based fallbacks.
 * 
 * Migration: 95.8% of candidates have primary_recruiter_id populated
 * Fallback: 4.2% still rely on name matching
 */

/**
 * Check if a candidate belongs to a specific recruiter
 * 
 * @param {Object} candidate - The candidate object
 * @param {Object} user - The user/recruiter object
 * @returns {boolean} True if candidate belongs to user
 */
export const isCandidateOwnedByUser = (candidate, user) => {
  if (!candidate || !user) return false;
  
  const userId = user.id;
  const userName = user.full_name || user.name || '';
  
  // PRIMARY: Check primary_recruiter_id (UUID) - Fast O(1) lookup
  if (candidate.primary_recruiter_id === userId) {
    return true;
  }
  
  // FALLBACK 1: Check candidate_owner name (Juno schema field)
  if (candidate.candidate_owner && candidate.candidate_owner === userName) {
    return true;
  }
  
  // FALLBACK 2: Check owner field (Bullhorn legacy)
  if (candidate.owner && candidate.owner === userName) {
    return true;
  }
  
  // FALLBACK 3: Check primary_recruiter_name (display name)
  if (candidate.primary_recruiter_name && candidate.primary_recruiter_name === userName) {
    return true;
  }
  
  return false;
};

/**
 * Check if a candidate belongs to any user in a list (for team views)
 * 
 * @param {Object} candidate - The candidate object
 * @param {Array} users - Array of user objects
 * @returns {boolean} True if candidate belongs to any user in the list
 */
export const isCandidateOwnedByAnyUser = (candidate, users) => {
  if (!candidate || !users || users.length === 0) return false;
  
  // Build sets for fast lookup
  const userIds = new Set(users.map(u => u.id).filter(Boolean));
  const userNames = new Set();
  users.forEach(u => {
    if (u.full_name) userNames.add(u.full_name);
    if (u.name && u.name !== u.full_name) userNames.add(u.name);
  });
  
  // PRIMARY: Check UUID first (95.8% of cases)
  if (candidate.primary_recruiter_id && userIds.has(candidate.primary_recruiter_id)) {
    return true;
  }
  
  // FALLBACK: Check names (4.2% of cases)
  const candidateOwnerName = candidate.candidate_owner || candidate.owner || candidate.primary_recruiter_name;
  if (candidateOwnerName && userNames.has(candidateOwnerName)) {
    return true;
  }
  
  return false;
};

/**
 * Get the display name of the recruiter who owns this candidate
 * 
 * @param {Object} candidate - The candidate object
 * @param {Array} allUsers - Array of all user objects (optional, for UUID lookup)
 * @returns {string} Display name of the recruiter or 'Unassigned'
 */
export const getCandidateRecruiterName = (candidate, allUsers = []) => {
  if (!candidate) return 'Unassigned';
  
  // If primary_recruiter_name is populated, use it (auto-synced from UUID)
  if (candidate.primary_recruiter_name) {
    return candidate.primary_recruiter_name;
  }
  
  // Try to look up by UUID if we have all users
  if (candidate.primary_recruiter_id && allUsers.length > 0) {
    const user = allUsers.find(u => u.id === candidate.primary_recruiter_id);
    if (user) {
      return user.full_name || user.name || 'Unknown';
    }
  }
  
  // Fall back to legacy fields
  if (candidate.candidate_owner) return candidate.candidate_owner;
  if (candidate.owner) return candidate.owner;
  
  return 'Unassigned';
};

/**
 * Filter candidates by recruiter(s) - Individual or Team view
 * 
 * @param {Array} candidates - Array of candidate objects
 * @param {Object|Array} userOrUsers - Single user object or array of users
 * @returns {Array} Filtered candidates
 */
export const filterCandidatesByRecruiter = (candidates, userOrUsers) => {
  if (!candidates || candidates.length === 0) return [];
  if (!userOrUsers) return candidates;
  
  // Handle single user
  if (!Array.isArray(userOrUsers)) {
    return candidates.filter(c => isCandidateOwnedByUser(c, userOrUsers));
  }
  
  // Handle multiple users (team view)
  return candidates.filter(c => isCandidateOwnedByAnyUser(c, userOrUsers));
};

/**
 * Get recruiter ID (UUID) from candidate - with fallback to name lookup
 * 
 * @param {Object} candidate - The candidate object
 * @param {Array} allUsers - Array of all user objects for name lookup
 * @returns {string|null} User UUID or null
 */
export const getCandidateRecruiterId = (candidate, allUsers = []) => {
  if (!candidate) return null;
  
  // PRIMARY: Return UUID if populated
  if (candidate.primary_recruiter_id) {
    return candidate.primary_recruiter_id;
  }
  
  // FALLBACK: Try to look up by name
  if (allUsers.length > 0) {
    const ownerName = candidate.candidate_owner || candidate.owner || candidate.primary_recruiter_name;
    if (ownerName) {
      const user = allUsers.find(u => 
        u.full_name === ownerName || u.name === ownerName
      );
      if (user) {
        return user.id;
      }
    }
  }
  
  return null;
};

/**
 * Build query filter for MongoDB to find candidates by recruiter
 * Useful for backend API queries
 * 
 * @param {string} userId - The user's UUID
 * @param {string} userName - The user's full name (fallback)
 * @returns {Object} MongoDB query filter
 */
export const buildRecruiterCandidateQuery = (userId, userName = null) => {
  const conditions = [];
  
  // PRIMARY: Match by UUID
  if (userId) {
    conditions.push({ primary_recruiter_id: userId });
  }
  
  // FALLBACK: Match by name
  if (userName) {
    conditions.push({ candidate_owner: userName });
    conditions.push({ owner: userName });
    conditions.push({ primary_recruiter_name: userName });
  }
  
  if (conditions.length === 0) return {};
  if (conditions.length === 1) return conditions[0];
  
  return { $or: conditions };
};

export default {
  isCandidateOwnedByUser,
  isCandidateOwnedByAnyUser,
  getCandidateRecruiterName,
  filterCandidatesByRecruiter,
  getCandidateRecruiterId,
  buildRecruiterCandidateQuery
};
